import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { PackagePropType } from '../../../prop-types/package';
import { MessageDescriptorPropType } from '../../../prop-types/intl';

import { ProductDetailsContext } from '../context';
import { messages } from '../messages';

import style from '../style.module.css';

const EMPTY_PACKAGE_ID = '';

const getDefaultText = (isInternational) => {
  if (isInternational) {
    return <FormattedMessage {...messages.defaultInternationalPackageName} />;
  }
  return <FormattedMessage {...messages.defaultPackageName} />;
};

const renderPackages = (packages, onPackageChanged) => {
  return packages.map(({ id, name }) => {
    const [packageTypeId] = id.split('-');
    return (
      <DropdownMenuItem key={id} onClick={() => onPackageChanged(packageTypeId)}>
        {name}
      </DropdownMenuItem>
    );
  });
};

const renderPackageGroups = (packageGroups, intl, onPackageChanged) => {
  return packageGroups.map(({ id, name, packages }) => (
    <DropdownMenuItemGroup key={id} label={intl.formatMessage(name, name?.values)}>
      {renderPackages(packages, onPackageChanged)}
    </DropdownMenuItemGroup>
  ));
};

export const ProductDetailsPackageDropdown = ({
  selectedPackageName = '',
  packageGroups,
  onPackageChanged,
  isEnabled = false,
  isInternational = false,
}) => {
  const intl = useIntl();
  const { userHasPermission } = useContext(ProductDetailsContext);
  const defaultPackageText = getDefaultText(isInternational);

  return (
    <DropdownMenu
      data-testid={isInternational ? 'international-package' : 'domestic-package'}
      disabled={!userHasPermission || !isEnabled}
      dropdownMenuShouldHaveWidthOfButton
      fullWidth
      title={selectedPackageName}
      variant='form'
    >
      <DropdownMenuToggler className={style['dropdown-picker']}>{selectedPackageName || defaultPackageText}</DropdownMenuToggler>
      <DropdownMenuItem onClick={() => onPackageChanged(EMPTY_PACKAGE_ID)}>
        <FormattedMessage {...messages.none} />
      </DropdownMenuItem>
      {renderPackageGroups(packageGroups, intl, onPackageChanged)}
    </DropdownMenu>
  );
};

ProductDetailsPackageDropdown.propTypes = {
  selectedPackageName: PropTypes.string,
  packageGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: MessageDescriptorPropType,
      packages: PropTypes.arrayOf(PackagePropType),
    }),
  ).isRequired,
  isEnabled: PropTypes.bool,
  onPackageChanged: PropTypes.func.isRequired,
  isInternational: PropTypes.bool,
};
